import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useWsEvent } from '@/auth/WsProvider';
import { useAuth, useCan } from '@/auth/AuthContext';
import AgreementFormModal from './AgreementFormModal';
import AgreementOnecLinkModal from './AgreementOnecLinkModal';
import ContactEditModal from './ContactEditModal';
import MainChargeCreateModal from './MainChargeCreateModal';
import SkudTab from './SkudTab';
import { eventText, eventIcon, fmtEventDateTime } from '@/lib/studentEvents';
import PaymentCreateModal from '@/pages/payments/PaymentCreateModal';
import PaymentDetailModal from '@/pages/payments/PaymentDetailModal';

/**
 * Карточка ученика. Открывается через URL `?student=ID`.
 * Закрывается через onClose (страница убирает параметр из URL).
 *
 * Сейчас отображаем только то, что уже есть в БД: личные данные, текущий класс,
 * классный руководитель, родители. Договоры/платежи/успеваемость — заглушки,
 * наполним по мере добавления соответствующих модулей.
 */
export default function StudentDetailModal({ studentId, canEdit, onEdit, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Вложенная модалка платежа: открывается из строк таблицы платежей в любой
  // вкладке (Обзор / Платежи). null = не открыта. Пока она открыта,
  // ESC и клик по overlay закрывают сначала её, а не всю карточку ученика.
  const [openPaymentId, setOpenPaymentId] = useState(null);

  const can = useCan();
  const canPaymentsWrite  = can('payments', 'write');
  const canPaymentsDelete = can('payments', 'delete');

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    setData(null);
    api.get(`/api/students/${studentId}`)
      .then((res) => { if (!aborted) setData(res.student); })
      .catch((e) => {
        if (aborted) return;
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить ученика');
      })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [studentId]);

  // ESC + блокировка скролла body. ESC: если открыта вложенная модалка
  // платежа — гасим её; иначе закрываем всю карточку. (PaymentDetailModal
  // в режиме nested свой ESC-listener не вешает — см. там.)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (openPaymentId !== null) setOpenPaymentId(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, openPaymentId]);

  // Push в 1С может закончиться уже после открытия карточки — слушаем событие
  // и обновляем секцию контрагента точечно, без перезагрузки всех данных.
  useWsEvent('onec.contractor.updated', (msg) => {
    setData((d) => {
      if (!d || !d.onecContractor || !msg?.contractor) return d;
      if (d.onecContractor.id !== msg.contractor.id) return d;
      return { ...d, onecContractor: msg.contractor };
    });
  });

  return (
    <div
      className="sd-overlay"
      onClick={(e) => {
        // Если поверх открыта payment-модалка — её overlay сама закроется
        // от своего клика; родительский overlay в это время не реагирует,
        // чтобы не закрылась вся карточка одним кликом.
        if (openPaymentId !== null) return;
        if (e.target.classList.contains('sd-overlay')) onClose();
      }}
    >
      <div className="sd-modal" role="dialog" aria-modal="true">
        {loading && <div className="sd-pane" style={{ padding: 32, color: 'var(--fg-muted)' }}>Загрузка…</div>}

        {!loading && error && (
          <div className="sd-pane" style={{ padding: 32 }}>
            <div style={{ color: 'var(--red-fg)', marginBottom: 12 }}>Ошибка: {error}</div>
            <button className="btn btn--sm" onClick={onClose}>Закрыть</button>
          </div>
        )}

        {!loading && !error && data && (
          <ModalBody
            data={data}
            tab={tab}
            setTab={setTab}
            onClose={onClose}
            canEdit={canEdit}
            onEdit={onEdit}
            onOpenPayment={setOpenPaymentId}
          />
        )}
      </div>

      {openPaymentId !== null && (
        <PaymentDetailModal
          paymentId={openPaymentId}
          canWrite={canPaymentsWrite}
          canDelete={canPaymentsDelete}
          onClose={() => setOpenPaymentId(null)}
          nested
        />
      )}
    </div>
  );
}

/**
 * Кнопка «Позвонить» в шапке карточки ученика. Номера берутся из
 * контактов (родителей и т.п.). По клику открывается мини-меню со
 * списком всех телефонов: ФИО контакта + номер + кнопки «Набрать» и
 * «Через АТС». Кнопка «Через АТС» отображается только если у текущего
 * пользователя задан `extPhone` (Настройки → Телефония + ext_phone
 * в карточке сотрудника).
 */
function CallButton({ contacts }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null); // номер, по которому идёт запрос
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Контакты с непустым телефоном — единственное, что нас интересует.
  const callable = (contacts || []).filter((c) => (c?.phone || '').trim() !== '');

  if (callable.length === 0) {
    return (
      <button
        className="btn btn--sm"
        disabled
        title="У контактов ученика не указаны телефоны"
      >
        <Icon name="phone" size={13} /> Позвонить
      </button>
    );
  }

  const callViaPbx = async (phone) => {
    setPending(phone);
    try {
      await api.post('/api/telephony/originate', { target: phone });
      setOpen(false);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось набрать через АТС');
    } finally {
      setPending(null);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn--sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Icon name="phone" size={13} /> Позвонить
      </button>
      {open && (
        <div className="sd-callmenu sd-callmenu--wide" role="menu">
          {callable.map((c) => (
            <div className="sd-callmenu__row" key={c.id || c.phone}>
              <div className="sd-callmenu__row-meta">
                <div className="sd-callmenu__row-name">{c.fullName || 'Контакт'}</div>
                <div className="sd-callmenu__row-phone">{c.phone}</div>
              </div>
              <div className="sd-callmenu__row-actions">
                <a
                  href={`tel:${c.phone}`}
                  className="btn btn--sm sd-callmenu__btn"
                  onClick={() => setOpen(false)}
                  title="Набрать (системная звонилка / мобильный)"
                >
                  <Icon name="phone" size={12} />
                </a>
                {user?.extPhone && (
                  <button
                    type="button"
                    className="btn btn--sm sd-callmenu__btn sd-callmenu__btn--pbx"
                    onClick={() => callViaPbx(c.phone)}
                    disabled={pending === c.phone}
                    title="Позвонить через АТС"
                  >
                    {pending === c.phone ? '…' : <Icon name="phone" size={12} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Кнопка «Написать» в шапке карточки ученика. По контактам ребёнка
 * собираем способы связи:
 *   • email → mailto:
 *   • телефон → Telegram (t.me/+…) и WhatsApp (wa.me/…) — внешние ссылки
 *   • если у контакта есть привязка к user-родителю (userId) — добавляем
 *     «Сообщение в ЛК» (открывается внутренний админский чат с этим
 *     родителем по ЭТОМУ ребёнку, через /api/chats/lookup → /messages).
 *
 * Если ни одного канала нет — кнопка disabled.
 */
function WriteButton({ contacts, studentId }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null); // contactId, для которого открываем ЛК
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const writable = (contacts || []).filter((c) =>
    (c?.email || '').trim() !== '' ||
    (c?.phone || '').trim() !== '' ||
    !!c?.userId
  );

  if (writable.length === 0) {
    return (
      <button className="btn btn--sm" disabled title="У контактов ученика нет ни телефона, ни email, ни ЛК">
        <Icon name="chat" size={13} /> Написать
      </button>
    );
  }

  const openChat = async (c) => {
    if (!c?.userId || !studentId) return;
    setPending(c.id);
    try {
      const res = await api.get(
        `/api/chats/lookup?parent_user_id=${c.userId}&student_id=${studentId}`
      );
      setOpen(false);
      if (res?.chatId) navigate(`/messages?chat=${res.chatId}`);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось открыть чат');
    } finally {
      setPending(null);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn--sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Icon name="chat" size={13} /> Написать
      </button>
      {open && (
        <div className="sd-callmenu sd-callmenu--wide" role="menu">
          {writable.map((c) => {
            const phoneClean = (c.phone || '').replace(/[^\d+]/g, '');
            const tgUrl = phoneClean ? `https://t.me/${phoneClean}` : null;
            // WhatsApp ждёт цифры без `+`.
            const waUrl = phoneClean ? `https://wa.me/${phoneClean.replace(/\+/g, '')}` : null;
            const mailUrl = c.email ? `mailto:${c.email}` : null;
            return (
              <div className="sd-callmenu__row" key={c.id || (c.email || c.phone)}>
                <div className="sd-callmenu__row-meta">
                  <div className="sd-callmenu__row-name">{c.fullName || 'Контакт'}</div>
                  <div className="sd-callmenu__row-phone">
                    {c.email || c.phone || (c.userId ? 'личный кабинет' : '')}
                  </div>
                </div>
                <div className="sd-callmenu__row-actions">
                  {mailUrl && (
                    <a
                      href={mailUrl}
                      className="btn btn--sm sd-callmenu__btn"
                      onClick={() => setOpen(false)}
                      title={`Письмо на ${c.email}`}
                    >
                      <Icon name="mail" size={12} />
                    </a>
                  )}
                  {tgUrl && (
                    <a
                      href={tgUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn--sm sd-callmenu__btn sd-callmenu__btn--tg"
                      onClick={() => setOpen(false)}
                      title={`Telegram: ${c.phone}`}
                    >
                      TG
                    </a>
                  )}
                  {waUrl && (
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn--sm sd-callmenu__btn sd-callmenu__btn--wa"
                      onClick={() => setOpen(false)}
                      title={`WhatsApp: ${c.phone}`}
                    >
                      WA
                    </a>
                  )}
                  {c.userId && (
                    <button
                      type="button"
                      className="btn btn--sm sd-callmenu__btn sd-callmenu__btn--lk"
                      onClick={() => openChat(c)}
                      disabled={pending === c.id}
                      title="Открыть чат в админской панели"
                    >
                      {pending === c.id ? '…' : 'ЛК'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ageSuffix(n) {
  if (n == null) return '';
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'год';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'года';
  return 'лет';
}

function formatBirth(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function ModalBody({ data, tab, setTab, onClose, canEdit, onEdit, onOpenPayment }) {
  const initials = ((data.firstName?.[0] || '') + (data.familyName?.[0] || '')).toUpperCase();
  const fio = `${data.familyName} ${data.firstName} ${data.midName || ''}`.trim();
  const sexRu = data.sex === 'female' ? 'женский' : data.sex === 'male' ? 'мужской' : '—';

  // Текущий долг ученика — лёгкий запрос, нужен только для индикатора в шапке.
  // Перезагружается при WS-событии payment.updated (если, скажем, проходит
  // оплата — долг должен уменьшиться без F5).
  const [debt, setDebt] = useState(null);
  const fetchDebt = () => {
    api.get(`/api/students/${data.id}/debt`)
      .then((res) => setDebt(Number(res.debt) || 0))
      .catch(() => setDebt(null));
  };
  useEffect(fetchDebt, [data.id]);
  useWsEvent('payment.updated', () => fetchDebt());

  // PaymentCreateModal с предзаполненными данными ученика.
  const [createPayOpen, setCreatePayOpen] = useState(null); // null | { yearId? }

  return (
    <>
      {/* === ШАПКА === */}
      <div className="sd-head">
        <div className="sd-head__left">
          <div className="sd-photo">
            {initials || '?'}
            {data.status === 'active' && <span className="sd-photo__status" title="Активен" />}
          </div>
          <div>
            <h2 className="sd-name">{fio}</h2>
            <div className="sd-meta">
              {data.class?.label && (
                <>
                  <span className="sd-meta__class">
                    <Icon name="users" size={11} />
                    {data.class.label}
                  </span>
                  <span className="sd-meta__dot">·</span>
                </>
              )}
              {data.age != null && (
                <>
                  <span>{data.age} {ageSuffix(data.age)}</span>
                  <span className="sd-meta__dot">·</span>
                </>
              )}
              <span className="sd-meta__id">#{data.id}</span>
              <span className="sd-meta__dot">·</span>
              <OnecBadge contractor={data.onecContractor} />
              {debt !== null && debt > 0 && (
                <>
                  <span className="sd-meta__dot">·</span>
                  <span className="sd-status sd-status--debt"
                        title="Сумма долга по основным договорам (school/kindergarten)">
                    <span className="sd-status__dot" />
                    Долг {fmtMoneyShort(debt)}
                  </span>
                </>
              )}
              {data.status === 'archive' && (
                <>
                  <span className="sd-meta__dot">·</span>
                  <span className="sd-status sd-status--debt">
                    <span className="sd-status__dot" />
                    Архив
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div />
        <div className="sd-head__actions">
          {canEdit && (
            <button className="btn btn--sm" onClick={onEdit}>
              <Icon name="edit" size={13} /> Редактировать
            </button>
          )}
          <CallButton contacts={data.contacts || []} />
          <WriteButton contacts={data.contacts || []} studentId={data.id} />

          <button className="btn btn--primary btn--sm"
                  onClick={() => setCreatePayOpen({})}
                  title="Создать новый счёт для этого ученика">
            <Icon name="plus" size={13} /> Создать счёт
          </button>
          <button className="sd-close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={14} />
          </button>
        </div>
      </div>

      {/* === ТЕЛО === */}
      <div className="sd-body">
        <aside className="sd-aside">
          <div className="sd-section">
            <div className="sd-section__title">Личные данные</div>
            <div className="sd-info-row">
              <span className="sd-info-row__label">Дата рожд.</span>
              <span className="sd-info-row__value">{formatBirth(data.birthdate)}</span>
            </div>
            <div className="sd-info-row">
              <span className="sd-info-row__label">Пол</span>
              <span className="sd-info-row__value">{sexRu}</span>
            </div>
            <div className="sd-info-row">
              <span className="sd-info-row__label">Класс</span>
              <span className="sd-info-row__value">
                {data.class?.label
                  ? `${data.class.label}${data.class.year ? ` · ${data.class.year}` : ''}`
                  : '—'}
              </span>
            </div>
            <div className="sd-info-row">
              <span className="sd-info-row__label">Кл. рук.</span>
              <span className="sd-info-row__value">{data.class?.teacher?.name || '—'}</span>
            </div>
            {data.phone && (
              <div className="sd-info-row">
                <span className="sd-info-row__label">Телефон</span>
                <span className="sd-info-row__value mono">{data.phone}</span>
              </div>
            )}
            {data.email && (
              <div className="sd-info-row">
                <span className="sd-info-row__label">Email</span>
                <span className="sd-info-row__value">{data.email}</span>
              </div>
            )}
          </div>

          <ContactsSection studentId={data.id} canEdit={canEdit} initialContacts={data.contacts || []} />
        </aside>

        <div className="sd-content">
          <div className="sd-tabs">
            <button className={'sd-tab' + (tab === 'overview'   ? ' is-active' : '')} onClick={() => setTab('overview')}>Обзор</button>
            <button className={'sd-tab' + (tab === 'extras'     ? ' is-active' : '')} onClick={() => setTab('extras')}>Доп. занятия</button>
            <button className={'sd-tab' + (tab === 'contracts'  ? ' is-active' : '')} onClick={() => setTab('contracts')}>Договоры</button>
            <button className={'sd-tab' + (tab === 'payments'   ? ' is-active' : '')} onClick={() => setTab('payments')}>Платежи</button>
            <button className={'sd-tab' + (tab === 'skud'       ? ' is-active' : '')} onClick={() => setTab('skud')}>СКУД</button>
            <button className={'sd-tab' + (tab === 'history'    ? ' is-active' : '')} onClick={() => setTab('history')}>История</button>
          </div>

          <div className="sd-pane">
            {tab === 'overview'   && (
              <OverviewTab
                studentId={data.id}
                onCreatePayment={(yearId) => setCreatePayOpen({ yearId })}
                onOpenPayment={onOpenPayment}
              />
            )}
            {tab === 'extras'     && <ExtrasTab studentId={data.id} />}
            {tab === 'contracts'  && <ContractsTab student={data} />}
            {tab === 'payments'   && <PaymentsTab studentId={data.id} onOpenPayment={onOpenPayment} />}
            {tab === 'skud'       && <SkudTab student={data} />}
            {tab === 'history'    && <HistoryTab studentId={data.id} />}
          </div>
        </div>
      </div>

      {/* Создание платежа: открываем PaymentCreateModal с предвыбранным
          учеником. Год / договор он подтянет автоматически через
          /api/payments/context — переключение там есть. */}
      {createPayOpen && (
        <PaymentCreateModal
          initialStudent={{
            id:         data.id,
            familyName: data.familyName,
            firstName:  data.firstName,
            midName:    data.midName,
            class:      data.class,
          }}
          initialYearStudyId={createPayOpen.yearId || null}
          onClose={() => setCreatePayOpen(null)}
          onCreated={(id) => {
            setCreatePayOpen(null);
            // Открываем сразу созданный платёж в новой вкладке списка платежей.
            window.open('/payments?open=' + id, '_blank', 'noopener');
            fetchDebt();
          }}
        />
      )}
    </>
  );
}

/**
 * Финансовый «Обзор» в карточке ученика. Структура — как на дизайне:
 *   • переключатель учебного года (показываем все годы, активный = с договором);
 *   • 3 KPI-карточки: Начислено за год / Оплачено / Задолженность;
 *   • список активных договоров на выбранный год;
 *   • 10 последних платежей по этим договорам.
 *
 * Источник — GET /api/students/{id}/overview?year_study_id=N.
 * KPI считаются ТОЛЬКО по основному договору (school/kindergarten); доп.
 * услуги отображаются как информация в списке договоров, но в KPI «Долг»
 * не входят — это бизнес-решение пользователя.
 */
function OverviewTab({ studentId, onCreatePayment, onOpenPayment }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearId, setYearId] = useState(null);
  // Бамп для триггера перезагрузки overview после создания/удаления
  // начислений в подвкладке. KPI «Начислено / Долг» зависят от main_charges,
  // поэтому при их изменении тут же освежаем сводку.
  const [mutCount, setMutCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const qs = yearId ? '?year_study_id=' + yearId : '';
    api.get(`/api/students/${studentId}/overview${qs}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (!yearId) setYearId(res.selectedYearId || null);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, yearId, mutCount]);

  if (loading && !data) {
    return <div className="sd-pane" style={{ padding: 32, color: 'var(--fg-muted)' }}>Загрузка…</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--red-fg)' }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  const k = data.kpi || {};

  return (
    <div className="sd-overview">
      {/* Переключатель учебного года. Показываем только годы, на которые
          у ученика есть хоть один договор — пустые в чипах не нужны.
          Сортируем по dateStart возрастанием (старые → новые), как во
          вкладке «Договоры». */}
      {(() => {
        const yearsWithAgr = (data.years || [])
          .filter((y) => y.hasAgreement)
          .slice()
          .sort((a, b) => String(a.dateStart || '').localeCompare(String(b.dateStart || '')));
        if (yearsWithAgr.length === 0) return null;
        return (
          <div className="sd-overview__years">
            <span className="sd-overview__years-lbl">УЧЕБНЫЙ ГОД</span>
            <div className="sd-overview__years-chips">
              {yearsWithAgr.map((y) => {
                const active = (data.selectedYearId || yearId) === y.id;
                return (
                  <button
                    key={y.id}
                    type="button"
                    className={'sd-year-chip' + (active ? ' is-active' : '')}
                    onClick={() => setYearId(y.id)}
                  >
                    {active && <span className="sd-year-chip__dot" />}
                    {y.name}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* KPI: Начислено / Оплачено / Долг */}
      <div className="sd-kpi">
        <div className="sd-kpi__cell">
          <div className="sd-kpi__label">НАЧИСЛЕНО ЗА ГОД</div>
          <div className="sd-kpi__value">{fmtMoneyShort(k.totalAccrued)}</div>
          {/* Начисления считаются ТОЛЬКО по основному договору (school/kindergarten);
              доп. услуги в эту сумму не входят (так договорено с пользователем). */}
          <div className="sd-kpi__sub">
            {k.mainAgreementId ? 'по основному договору' : 'нет основного договора'}
          </div>
        </div>
        <div className="sd-kpi__cell sd-kpi__cell--ok">
          <div className="sd-kpi__label">ОПЛАЧЕНО</div>
          <div className="sd-kpi__value">{fmtMoneyShort(k.totalPaid)}</div>
          {k.totalAccrued > 0 && (
            <div className="sd-kpi__bar">
              <div
                className="sd-kpi__bar-fill"
                style={{ width: Math.min(100, Math.round((k.totalPaid / k.totalAccrued) * 100)) + '%' }}
              />
            </div>
          )}
          {/* Вступительный взнос — отдельный показатель, не суммируется с
              ежемесячными. Цвет плашки зависит от статуса:
                unpaid  → красный   (не оплачен ни рубля)
                partial → оранжевый (часть)
                paid    → зелёный   (полностью)
              Если у договора нет взноса — блок не рендерится. */}
          {k.admissionFeeStatus && k.admissionFeeStatus !== 'none' && (
            <div className={'sd-kpi__admission sd-kpi__admission--' + k.admissionFeeStatus}>
              <span className="sd-kpi__admission-label">Вступительный взнос:</span>
              <span className="sd-kpi__admission-val">
                {fmtMoneyShort(k.admissionFeePaid)}
                <span className="muted"> / {fmtMoneyShort(k.admissionFee)}</span>
              </span>
            </div>
          )}
        </div>
        <div className={'sd-kpi__cell ' + (k.debt > 0 ? 'sd-kpi__cell--err' : 'sd-kpi__cell--mute')}>
          <div className="sd-kpi__label">ЗАДОЛЖЕННОСТЬ</div>
          <div className="sd-kpi__value">{fmtMoneyShort(k.debt)}</div>
          <div className="sd-kpi__sub">{k.debt > 0 ? 'требует оплаты' : 'нет долга'}</div>
        </div>
      </div>

      {/* Активные договоры на выбранный год */}
      <div className="sd-section-head">
        <h3>АКТИВНЫЕ ДОГОВОРЫ</h3>
      </div>
      {data.agreements.length === 0 && (
        <div className="ref-empty" style={{ padding: 16 }}>На выбранный год договоров нет.</div>
      )}
      <div className="sd-overview__agrs">
        {data.agreements.map((a) => <OverviewAgreement key={a.id} a={a} />)}
      </div>

      {/* Платежи + Начисления — в виде двух вкладок одного блока. */}
      <PaymentsAndChargesBlock
        recentPayments={data.recentPayments}
        mainAgreementId={data.kpi?.mainAgreementId || null}
        onCreatePayment={onCreatePayment}
        selectedYearId={data.selectedYearId}
        onChargesMutated={() => setMutCount((c) => c + 1)}
        onOpenPayment={onOpenPayment}
      />
    </div>
  );
}

/**
 * Вкладка «Доп. занятия» в карточке ученика.
 *
 * Бизнес-кейс: пользователь хочет видеть, сколько ребёнок оплатил по каждому
 * виду доп. занятия (например, «Программирование (индивидуально)»), сколько
 * фактически списалось проведёнными уроками и каков остаток. И в рублях,
 * и в количестве занятий.
 *
 * Источник данных: GET /api/students/{id}/extras-summary?year_study_id=N.
 * Год по умолчанию выбирается на бэке (текущий с данными → ближайший).
 * Пустые годы не показываем — как и на «Обзоре» с договорами.
 */
function ExtrasTab({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearId, setYearId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const qs = yearId ? '?year_study_id=' + yearId : '';
    api.get(`/api/students/${studentId}/extras-summary${qs}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (!yearId) setYearId(res.selectedYearId || null);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, yearId]);

  if (loading && !data) {
    return <div className="sd-pane" style={{ padding: 32, color: 'var(--fg-muted)' }}>Загрузка…</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--red-fg)' }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  const rows   = data.rows || [];
  const totals = data.totals || {};

  // Учебные годы — оставляем только те, где есть данные. Сортируем
  // по dateStart возрастанием (старые → новые), как во вкладке «Обзор».
  const yearsWithData = (data.years || [])
    .filter((y) => y.hasData)
    .slice()
    .sort((a, b) => String(a.dateStart || '').localeCompare(String(b.dateStart || '')));

  return (
    <div className="sd-overview">
      {yearsWithData.length > 0 && (
        <div className="sd-overview__years">
          <span className="sd-overview__years-lbl">УЧЕБНЫЙ ГОД</span>
          <div className="sd-overview__years-chips">
            {yearsWithData.map((y) => {
              const active = (data.selectedYearId || yearId) === y.id;
              return (
                <button
                  key={y.id}
                  type="button"
                  className={'sd-year-chip' + (active ? ' is-active' : '')}
                  onClick={() => setYearId(y.id)}
                >
                  {active && <span className="sd-year-chip__dot" />}
                  {y.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="ref-empty" style={{ padding: 32 }}>
          {yearsWithData.length === 0
            ? 'У ученика пока нет ни оплат, ни проведённых доп. занятий.'
            : 'На выбранный учебный год нет ни оплат, ни проведённых занятий.'}
        </div>
      ) : (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Предмет</th>
                <th style={{ width: 200, textAlign: 'right' }}>Оплачено, руб (занятий)</th>
                <th style={{ width: 200, textAlign: 'right' }}>Потрачено, руб (занятий)</th>
                <th style={{ width: 200, textAlign: 'right' }}>Остаток, руб (занятий)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.label}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {fmtNumPlain(r.paidAmount)} <span className="muted">({fmtCount(r.paidCount)})</span>
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {fmtNumPlain(r.spentAmount)} <span className="muted">({r.spentCount})</span>
                  </td>
                  <td className="mono" style={{
                    textAlign: 'right',
                    color: r.remainingAmount > 0 ? 'var(--green-fg)'
                         : r.remainingAmount < 0 ? 'var(--red-fg)'
                         : undefined,
                    fontWeight: 600,
                  }}>
                    {fmtNumPlain(r.remainingAmount)} <span className="muted">({fmtCount(r.remainingCount)})</span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="pay-table-total">
                <td>Итого</td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {fmtNumPlain(totals.paidAmount)} <span className="muted">({fmtCount(totals.paidCount)})</span>
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {fmtNumPlain(totals.spentAmount)} <span className="muted">({totals.spentCount})</span>
                </td>
                <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                  {fmtNumPlain(totals.remainingAmount)} <span className="muted">({fmtCount(totals.remainingCount)})</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Вкладка «Платежи» в карточке ученика.
 *
 * Простая структура — переключатель учебных годов + полный список платежей,
 * привязанных к договорам выбранного года через payment_items.agreement_id.
 * Клик по строке — открыть платёж в общем списке (там же модалка деталей).
 *
 * Источник: GET /api/students/{id}/payments?year_study_id=N|none.
 *   • Год по умолчанию — текущий с данными → ближайший с данными → «без года»
 *     если только орфаны → самый свежий из всех.
 *   • Псевдо-год «Без года» появляется в списке, только если у ученика
 *     реально есть платежи по договорам без year_study_id.
 */
function PaymentsTab({ studentId, onOpenPayment }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearId, setYearId] = useState(null); // number | 'none' | null

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const qs = yearId !== null && yearId !== undefined ? '?year_study_id=' + yearId : '';
    api.get(`/api/students/${studentId}/payments${qs}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (yearId === null) {
          // Бэк сам выбрал — синхронизируем локальный стейт, чтобы клик на
          // кнопку «текущий» / «без года» рендерился правильно.
          setYearId(res.selectedYearId !== undefined ? res.selectedYearId : null);
        }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, yearId]);

  if (loading && !data) {
    return <div className="sd-pane" style={{ padding: 32, color: 'var(--fg-muted)' }}>Загрузка…</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--red-fg)' }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  const rows = data.rows || [];

  // Только годы с платежами + опциональный псевдо-год «Без года».
  // Сортируем по dateStart возрастанием — как на «Обзоре» и «Доп. занятиях».
  const yearsWithData = (data.years || [])
    .filter((y) => y.hasData)
    .slice()
    .sort((a, b) => String(a.dateStart || '').localeCompare(String(b.dateStart || '')));
  const chips = data.hasOrphan
    ? [...yearsWithData, { id: 'none', name: 'Без года' }]
    : yearsWithData;

  const selectedKey = data.selectedYearId !== undefined ? data.selectedYearId : yearId;

  return (
    <div className="sd-overview">
      {chips.length > 0 && (
        <div className="sd-overview__years">
          <span className="sd-overview__years-lbl">УЧЕБНЫЙ ГОД</span>
          <div className="sd-overview__years-chips">
            {chips.map((y) => {
              const active = String(selectedKey) === String(y.id);
              return (
                <button
                  key={String(y.id)}
                  type="button"
                  className={'sd-year-chip' + (active ? ' is-active' : '')}
                  onClick={() => setYearId(y.id)}
                >
                  {active && <span className="sd-year-chip__dot" />}
                  {y.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="ref-empty" style={{ padding: 32 }}>
          {chips.length === 0
            ? 'У ученика пока нет ни одного платежа.'
            : 'На выбранный учебный год платежей не найдено.'}
        </div>
      ) : (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 130 }}>Дата</th>
                <th>Назначение</th>
                <th style={{ width: 120, textAlign: 'right' }}>Сумма</th>
                <th style={{ width: 120 }}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <OverviewPaymentRow key={p.id} p={p} onOpen={onOpenPayment} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Количество занятий: целое, если можно — иначе с одним знаком после запятой.
// quantity в payment_items хранится как DECIMAL(10,3), на практике почти
// всегда целое (одно занятие = 1), но иногда дробное (например, «полмесяца»).
function fmtCount(n) {
  const v = Number(n) || 0;
  return Math.abs(v - Math.round(v)) < 0.005 ? String(Math.round(v)) : v.toFixed(1);
}

// Сумма без символа валюты — для таблицы доп. занятий. В заголовке
// колонок уже написано «руб», дублировать «₽» в ячейке не нужно.
function fmtNumPlain(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function PaymentsAndChargesBlock({ recentPayments, mainAgreementId, onCreatePayment, selectedYearId, onChargesMutated, onOpenPayment }) {
  const [tab, setTab] = useState('payments');
  const can = useCan();
  const canWrite = can('extra.charges', 'write');

  // Контекст начислений (rows + agreement + usedMonths) грузим в parent —
  // эти данные нужны и таблице, и кнопке «Создать начисление» (она должна
  // знать границы периода договора и список занятых месяцев). Лежит на
  // верхнем уровне, чтобы reload сразу после удаления/создания работал
  // одновременно для всех потребителей.
  const [chargesData, setChargesData]   = useState(null);   // { rows, agreement, usedMonths } | null
  const [chargesError, setChargesError] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [createOpen, setCreateOpen]     = useState(false);

  const reloadCharges = React.useCallback(() => {
    if (!mainAgreementId) return;
    setChargesError(null);
    setChargesLoading(true);
    api.get('/api/charges/main-monthly/by-agreement?agreement_id=' + mainAgreementId)
      .then((res) => setChargesData({
        rows:        res.rows       || [],
        agreement:   res.agreement  || null,
        usedMonths:  res.usedMonths || [],
      }))
      .catch((e) => setChargesError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setChargesLoading(false));
  }, [mainAgreementId]);

  // Зовётся после реального изменения main_charges (create / delete) —
  // обновляет локальный список и параллельно бьёт по родителю, чтобы тот
  // переподтянул overview (KPI «Начислено / Долг» зависят от main_charges).
  const onMutated = React.useCallback(() => {
    reloadCharges();
    onChargesMutated && onChargesMutated();
  }, [reloadCharges, onChargesMutated]);

  // Оптимистично выкидываем строку из локального стейта — до того, как
  // долетит фоновый GET. Иначе при удалении (особенно с пинком в 1С —
  // он медленный) пользователь несколько секунд видит «удалённую» строку
  // в списке, что выглядит как баг.
  const removeChargeLocal = React.useCallback((id) => {
    setChargesData((d) =>
      d ? { ...d, rows: (d.rows || []).filter((r) => r.id !== id) } : d
    );
  }, []);

  // Тянем данные при первом переходе на вкладку, и пере-тянем если сменился
  // основной договор (например, переключили учебный год).
  useEffect(() => {
    if (tab !== 'charges' || !mainAgreementId) return;
    if (chargesData && chargesData.agreement?.id === mainAgreementId) return;
    reloadCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, mainAgreementId]);

  return (
    // minHeight удерживает блок от «схлопывания» при переключении вкладок,
    // когда контент в свежей вкладке ещё грузится. Без этого страница
    // отмматывается вверх — ставим запас на ~5 строк таблицы.
    <div style={{ marginTop: 16, minHeight: 320 }}>
      <div className="sd-section-head">
        <div className="sd-subtabs">
          <button
            type="button"
            className={'sd-subtab' + (tab === 'payments' ? ' is-active' : '')}
            onClick={() => setTab('payments')}
          >
            Последние платежи
          </button>
          <button
            type="button"
            className={'sd-subtab' + (tab === 'charges' ? ' is-active' : '')}
            onClick={() => setTab('charges')}
            disabled={!mainAgreementId}
            title={mainAgreementId ? 'Начисления по основному договору' : 'Нет основного договора на этот год'}
          >
            Начисления
          </button>
        </div>
        {tab === 'payments' && onCreatePayment && selectedYearId && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => onCreatePayment(selectedYearId)}
          >
            <Icon name="plus" size={11} /> Новый счёт
          </button>
        )}
        {tab === 'charges' && canWrite && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setCreateOpen(true)}
            disabled={!chargesData?.agreement}
            title={chargesData?.agreement ? 'Добавить ручное начисление' : 'Загрузка договора…'}
          >
            <Icon name="plus" size={11} /> Создать начисление
          </button>
        )}
      </div>

      {tab === 'payments' && (
        recentPayments.length === 0 ? (
          <div className="ref-empty" style={{ padding: 16 }}>Платежей по этим договорам ещё нет.</div>
        ) : (
          <div className="table-wrap">
            <table className="table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Дата</th>
                  <th>Назначение</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Сумма</th>
                  <th style={{ width: 110 }}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {recentPayments.map((p) => (
                  <OverviewPaymentRow key={p.id} p={p} onOpen={onOpenPayment} />
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'charges' && mainAgreementId && (
        <ChargesList
          rows={chargesData?.rows ?? null}
          loading={chargesLoading}
          error={chargesError}
          canWrite={canWrite}
          onMutated={onMutated}
          onLocalRemove={removeChargeLocal}
        />
      )}

      {createOpen && chargesData?.agreement && (
        <MainChargeCreateModal
          agreement={chargesData.agreement}
          usedMonths={chargesData.usedMonths || []}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); onMutated(); }}
        />
      )}
    </div>
  );
}

/**
 * Презентационный список начислений по основному договору. Данные приходят
 * сверху из PaymentsAndChargesBlock — он же владеет контекстом договора
 * (нужен кнопке «Создать начисление»). Удаление здесь же: на каждой строке
 * крестик. Если начисление выгружено в 1С — confirm-диалог напоминает,
 * что в 1С документ нужно удалить вручную.
 */
function ChargesList({ rows, loading, error, canWrite, onMutated, onLocalRemove }) {
  const [busyId, setBusyId]   = useState(null);
  const [delError, setDelError] = useState(null);

  const onDelete = async (row) => {
    let confirmText;
    if (row.onecUploadedAt && row.onecDocumentId) {
      confirmText =
        'Удалить начисление за ' + ruMonth(row.month) + '?\n\n' +
        'Документ 1С «' + row.onecDocumentId + '» (реализация услуг) тоже будет удалён — ' +
        'CRM пометит его на удаление через расширение 1С. ' +
        (row.onecInvoiceNumber
          ? 'Связанный счёт-фактура № ' + row.onecInvoiceNumber + ' тоже будет помечен на удаление.\n\n'
          : '\n') +
        'Если 1С недоступна — удаление прервётся, начисление останется в CRM, ' +
        'можно будет повторить позже.';
    } else {
      confirmText = 'Удалить начисление за ' + ruMonth(row.month) + '?';
    }
    if (!window.confirm(confirmText)) return;

    setBusyId(row.id); setDelError(null);
    try {
      const res = await api.delete('/api/charges/main-monthly/' + row.id);
      // Оптимистичный апдейт: убираем строку сразу, не дожидаясь
      // фонового reload. Иначе при удалении с дёрганьем 1С пользователь
      // ещё пару секунд видит «удалённую» строку в списке.
      onLocalRemove && onLocalRemove(row.id);
      if (res?.wasUploaded && res.onecForce) {
        alert('Начисление удалено в CRM, но документ ' + res.onecDocumentId
          + ' в 1С остался (режим force). Удалите его в 1С вручную.');
      }
      onMutated && onMutated();
    } catch (e) {
      // 502 onec_delete_failed → специальное предложение force=1.
      if (e instanceof ApiError && e.code === 'onec_delete_failed') {
        const msg = e.message + '\n\nПопробовать снова — или удалить только в CRM (документ в 1С потом удалите вручную)?';
        if (window.confirm(msg + '\n\nOK — удалить только в CRM (force).\nОтмена — оставить как есть.')) {
          try {
            const res = await api.delete('/api/charges/main-monthly/' + row.id + '?force=1');
            onLocalRemove && onLocalRemove(row.id);
            if (res?.onecDocumentId) {
              alert('Начисление удалено в CRM. Документ ' + res.onecDocumentId + ' в 1С остался — удалите его вручную.');
            }
            onMutated && onMutated();
          } catch (e2) {
            setDelError(e2 instanceof ApiError ? e2.message : 'Не удалось удалить');
          }
        }
      } else {
        setDelError(e instanceof ApiError ? e.message : 'Не удалось удалить');
      }
    } finally {
      setBusyId(null);
    }
  };

  // Заглушки занимают то же место, что таблица — иначе при первом
  // переключении блок «схлопывается» и страница перематывается вверх.
  const stubStyle = { padding: 16, minHeight: 240 };
  const shownError = delError || error;
  if (loading && rows === null) {
    return <div className="ref-empty" style={{ ...stubStyle, color: 'var(--fg-muted)' }}>Загрузка…</div>;
  }
  if (shownError && rows === null) {
    return <div style={{ ...stubStyle, color: 'var(--red-fg)' }}>Ошибка: {shownError}</div>;
  }
  if ((rows || []).length === 0) {
    return <div className="ref-empty" style={stubStyle}>Начислений по договору пока нет.</div>;
  }

  return (
    <>
      {shownError && (
        <div style={{ color: 'var(--red-fg)', fontSize: 12, padding: '6px 0' }}>
          Ошибка: {shownError}
        </div>
      )}
      <div className="table-wrap">
      <table className="table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ width: 90 }}>Месяц</th>
            <th>Период / формула</th>
            <th style={{ width: 110, textAlign: 'right' }}>Сумма</th>
            <th style={{ width: 130 }}>1С</th>
            {canWrite && <th style={{ width: 36 }} />}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => {
            const isUploaded = !!r.onecUploadedAt;
            return (
              <tr key={r.id}>
                <td className="mono">{r.month}</td>
                <td className="muted" style={{ fontSize: 11.5 }}>
                  {r.isManual ? (
                    <span className="pay-st pay-st--mute" style={{ fontSize: 10.5, marginRight: 6 }}
                          title="Создано вручную (не пересчитывается массовым начислением)">
                      ручное
                    </span>
                  ) : null}
                  {r.isFullMonth ? (
                    <>Полный месяц по договору</>
                  ) : (
                    <>
                      {fmtDateRu(r.rangeFrom)} — {fmtDateRu(r.rangeTo)}
                      {r.workingDaysInMonth > 0 && (
                        <span> · {r.workingDaysInRange} / {r.workingDaysInMonth} раб. дн.</span>
                      )}
                    </>
                  )}
                  {r.recomputedCount > 0 && (
                    <span className="muted" style={{ fontSize: 10.5 }}> · пересчитано {r.recomputedCount}×</span>
                  )}
                  {r.comment && (
                    <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                      «{r.comment}»
                    </div>
                  )}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>{fmtMoneyShort(r.amount)}</td>
                <td>
                  {isUploaded
                    ? <span className="pay-st pay-st--ok"
                            title={'Выгружено: ' + (r.onecUploadedAt || '')}>{r.onecDocumentId || 'выгружено'}</span>
                    : <span className="pay-st pay-st--mute">не выгружено</span>}
                </td>
                {canWrite && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      style={{ padding: 2, height: 24, color: 'var(--red-fg)' }}
                      title="Удалить начисление"
                      disabled={busyId === r.id}
                      onClick={() => onDelete(r)}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}

function ruMonth(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || '';
  const months = ['январь','февраль','март','апрель','май','июнь',
                  'июль','август','сентябрь','октябрь','ноябрь','декабрь'];
  const [y, m] = ym.split('-');
  return `${months[(parseInt(m, 10) - 1) || 0]} ${y}`;
}

function OverviewAgreement({ a }) {
  const meta = TYPE_META[a.type] || { label: a.type, icon: 'doc' };
  return (
    <div className="sd-overview__agr">
      <div className="sd-overview__agr-icon">
        <Icon name={meta.icon} size={18} />
      </div>
      <div className="sd-overview__agr-body">
        <div className="sd-overview__agr-row">
          <span className="sd-overview__agr-title">{meta.title || meta.label}</span>
          <span className="agr-row__type-pill">{meta.label}</span>
          {a.class !== null && a.class !== undefined && (
            // В agreements.class хранится только НОМЕР класса (1..4); литера
            // привязки (А/Б/В) живёт в students_classes и может меняться,
            // поэтому на самом договоре её не показываем.
            <span className="agr-row__type-pill agr-row__type-pill--neutral">{a.class} класс</span>
          )}
        </div>
        <div className="sd-overview__agr-meta">
          <span className="muted">№ {a.displayNumber || a.number}</span>
          <span className="muted">{fmtDateRu(a.dateStart)} — {fmtDateRu(a.dateEnd)}</span>
          <span className={'pay-st ' + (
            a.dateTermination ? 'pay-st--mute' : 'pay-st--ok'
          )}>
            {a.dateTermination
              ? (a.dateStart && a.dateTermination <= a.dateStart ? 'аннулирован' : 'расторгнут')
              : 'действует'}
          </span>
          {a.onecLinked
            ? <span className="muted mono">∞ 1С: {a.onecCode || '—'}</span>
            : <span className="muted">не привязан к 1С</span>}
        </div>
      </div>
      <div className="sd-overview__agr-amount">
        {a.tariffPrice
          ? <>
              <div className="sd-overview__agr-price">{fmtMoneyShort(a.tariffPrice)}</div>
              <div className="muted" style={{ fontSize: 11 }}>в месяц</div>
            </>
          : null}
        {!!a.admissionFee && (
          <div className="muted" style={{ fontSize: 11 }}>
            взнос {fmtMoneyShort(a.admissionFee)}
          </div>
        )}
      </div>
    </div>
  );
}

const KIND_LABELS_OVERVIEW = {
  school_admission_fee: 'Вступит. (школа)',
  school_monthly:       'Ежем. (школа)',
  kg_admission_fee:     'Вступит. (сад)',
  kg_monthly:           'Ежем. (сад)',
  extra_lesson:         'Доп. занятие',
  extra_camp:           'Доп. лагерь',
  manual:               'Прочее',
};

const STATUS_LABELS_OVERVIEW = {
  draft:     { label: 'Черновик', cls: 'pay-st pay-st--draft' },
  pending:   { label: 'Ожидает',  cls: 'pay-st pay-st--pending' },
  succeeded: { label: 'Оплачен',  cls: 'pay-st pay-st--ok' },
  failed:    { label: 'Ошибка',   cls: 'pay-st pay-st--err' },
  canceled:  { label: 'Отменён',  cls: 'pay-st pay-st--mute' },
};

function OverviewPaymentRow({ p, onOpen }) {
  const s = STATUS_LABELS_OVERVIEW[p.status] || { label: p.status, cls: 'pay-st' };
  const kinds = (p.kinds || []).map((k) => KIND_LABELS_OVERVIEW[k] || k).join(', ') || '—';
  // Если родитель передал onOpen — открываем модалку платежа поверх карточки
  // ученика (без перехода на /payments). Иначе fallback: открыть в той же
  // вкладке через старую ссылку — на случай, если строка использована
  // где-то ещё, где обработчика нет.
  const onClick = onOpen
    ? () => onOpen(p.id)
    : () => window.open('/payments?open=' + p.id, '_self');
  return (
    <tr style={{ cursor: 'pointer' }} onClick={onClick}>
      <td className="mono" style={{ fontSize: 11 }}>
        {p.createdAt ? p.createdAt.replace('T', ' ').slice(0, 16) : '—'}
      </td>
      <td className="muted">{kinds}</td>
      <td className="mono" style={{ textAlign: 'right' }}>{fmtMoneyShort(p.amount)}</td>
      <td><span className={s.cls}>{s.label}</span></td>
    </tr>
  );
}

function fmtMoneyShort(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₽';
}
function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function KV({ k, v }) {
  return (
    <div style={{
      padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--panel-2)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{k}</div>
      <div style={{ fontSize: 13, color: 'var(--fg)' }}>{v}</div>
    </div>
  );
}

const TYPE_META = {
  school:       { label: 'Школа',       icon: 'graduation', title: 'Основной договор обучения' },
  kindergarten: { label: 'Детский сад', icon: 'users',      title: 'Договор детского сада' },
  paid_services: { label: 'Доп. услуги', icon: 'sparkle',    title: 'Договор на доп. услуги' },
};

function fmtDateRuShort(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
function todayIsoStr() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Договоры ученика, сгруппированные по учебным годам — переключатель «пиллами» вверху,
 * как в дизайне. Без года — отдельная вкладка «Без года».
 *
 * Источник — локальная таблица agreements (бэк уже отдаёт yearStudyId/yearStudyName в JSON).
 * Live-обновления статусов 1С — через WS-событие onec.agreement.updated.
 */
function ContractsTab({ student }) {
  const can = useCan();
  const canWrite = can('agreements', 'write');

  const [agreements, setAgreements] = useState(null);
  const [allYears, setAllYears]     = useState([]);   // справочник учебных годов из /api/years_study
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [createType, setCreateType] = useState(null);
  const [editing, setEditing]       = useState(null);
  const [linking, setLinking]       = useState(null);
  const [activeYearId, setActiveYearId] = useState(null); // number | 'none' | null=ещё не выбран

  useEffect(() => {
    if (!student?.id) return;
    let aborted = false;
    setLoading(true); setError(null);
    Promise.all([
      api.get(`/api/agreements?student_id=${student.id}`),
      api.get('/api/years_study'),
    ]).then(([a, y]) => {
      if (aborted) return;
      setAgreements(a.rows || []);
      setAllYears((y.rows || []).filter((yy) => !yy.archive));
    }).catch((e) => {
      if (!aborted) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
    }).finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [student?.id]);

  useWsEvent('onec.agreement.updated', (msg) => {
    if (!msg?.agreement) return;
    setAgreements((prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((a) => a.onec?.agreementId === msg.agreement.id);
      if (idx < 0) return prev;
      const next = prev.slice();
      next[idx] = {
        ...next[idx],
        onec: {
          agreementId: msg.agreement.id,
          code:        msg.agreement.code,
          syncState:   msg.agreement.syncState,
          syncError:   msg.agreement.syncError,
        },
      };
      return next;
    });
  });

  // Множество годов, в которых у ученика есть договоры (для фильтра «прошлые-пустые скрываем»)
  const yearsWithAgreements = useMemo(() => {
    if (!agreements) return new Set();
    const s = new Set();
    for (const a of agreements) if (a.yearStudyId) s.add(a.yearStudyId);
    return s;
  }, [agreements]);

  const today = todayIsoStr();

  // Текущий год — тот, что покрывает сегодняшнюю дату.
  const currentYear = useMemo(
    () => allYears.find((y) => y.dateStart && y.dateEnd && y.dateStart <= today && today <= y.dateEnd) || null,
    [allYears, today]
  );

  // Табы: показываем будущие и текущий год всегда; прошлые — только если у ученика
  // есть в них договор. Сортировка от ранних к поздним.
  const yearTabs = useMemo(() => {
    const visible = allYears.filter((y) => {
      const isPast = y.dateEnd && y.dateEnd < today;
      if (isPast) return yearsWithAgreements.has(y.id);
      return true;
    });
    visible.sort((a, b) => (a.dateStart || '').localeCompare(b.dateStart || ''));

    // Если у ученика есть договоры с yearStudyId, который НЕ в allYears (например, год отсутствует
    // в справочнике или archive=1) — добавим его как «id #N» в нужном месте.
    const knownIds = new Set(visible.map((y) => y.id));
    const orphanIds = [...yearsWithAgreements].filter((id) => !knownIds.has(id));
    for (const oid of orphanIds) {
      const a = agreements.find((x) => x.yearStudyId === oid);
      visible.push({ id: oid, name: a?.yearStudyName || `год #${oid}`, dateStart: '', dateEnd: '' });
    }

    // Договоры без года — вкладка «Без года» в конце.
    if (agreements && agreements.some((a) => !a.yearStudyId)) {
      visible.push({ id: 'none', name: 'Без года', dateStart: '', dateEnd: '' });
    }
    return visible;
  }, [allYears, yearsWithAgreements, today, agreements]);

  // Дефолтная вкладка — текущий год (если он есть среди табов и валиден),
  // иначе самый поздний из доступных, иначе 'none' если такая есть.
  useEffect(() => {
    if (activeYearId !== null) return;
    if (yearTabs.length === 0) return;
    if (currentYear && yearTabs.some((y) => y.id === currentYear.id)) {
      setActiveYearId(currentYear.id);
    } else {
      setActiveYearId(yearTabs[yearTabs.length - 1].id);
    }
  }, [yearTabs, currentYear, activeYearId]);

  const filtered = useMemo(() => {
    if (!agreements) return [];
    if (activeYearId === 'none') return agreements.filter((a) => !a.yearStudyId);
    if (activeYearId == null)    return [];
    return agreements.filter((a) => a.yearStudyId === activeYearId);
  }, [agreements, activeYearId]);

  if (loading) return <div style={{ padding: 24, color: 'var(--fg-muted)' }}>Загрузка…</div>;
  if (error)   return <div style={{ padding: 24, color: 'var(--red-fg)' }}>Ошибка: {error}</div>;

  return (
    <>
      {/* Переключатель учебных годов */}
      {yearTabs.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{
            fontSize: 11, color: 'var(--fg-faint)', textTransform: 'uppercase',
            letterSpacing: '0.06em', fontWeight: 600,
          }}>Учебный год</span>
          <div className="sd-years">
            {yearTabs.map((y) => {
              const isCurrent = currentYear && y.id === currentYear.id;
              return (
                <button
                  key={y.id}
                  type="button"
                  className={'sd-year-btn' + (activeYearId === y.id ? ' is-active' : '')}
                  onClick={() => setActiveYearId(y.id)}
                  title={isCurrent ? 'Текущий учебный год' : undefined}
                >
                  {isCurrent && <span className="sd-year-btn__dot" />}
                  {y.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {filtered.length > 0 ? (
        <div className="agr-list">
          {filtered.map((a) => (
            <AgreementRow
              key={a.id}
              a={a}
              canEdit={canWrite}
              onEdit={() => setEditing(a)}
              onOnec={() => setLinking(a)}
            />
          ))}
        </div>
      ) : (
        <Empty
          icon="doc"
          title={agreements && agreements.length === 0 ? 'Договоров нет' : 'Нет договоров за этот год'}
          text="Добавьте новый по кнопкам ниже."
        />
      )}

      {canWrite && (
        <div className="agr-add-row">
          <button className="btn btn--sm" onClick={() => setCreateType('school')}>
            <Icon name="graduation" size={13} /> + Школа
          </button>
          <button className="btn btn--sm" onClick={() => setCreateType('kindergarten')}>
            <Icon name="users" size={13} /> + Детский сад
          </button>
          <button className="btn btn--sm" onClick={() => setCreateType('paid_services')}>
            <Icon name="sparkle" size={13} /> + Доп. услуги
          </button>
        </div>
      )}

      {createType && (
        <AgreementFormModal
          mode="create"
          student={student}
          defaultType={createType}
          onClose={() => setCreateType(null)}
          onSaved={(a) => setAgreements((prev) => [a, ...(prev || [])])}
        />
      )}
      {editing && (
        <AgreementFormModal
          mode="edit"
          student={student}
          agreement={editing}
          onClose={() => setEditing(null)}
          onSaved={(a) => setAgreements((prev) => prev.map((r) => r.id === a.id ? a : r))}
          onDeleted={(id) => setAgreements((prev) => (prev || []).filter((r) => r.id !== id))}
          onLink={(a) => setLinking(a)}
        />
      )}
      {linking && (
        <AgreementOnecLinkModal
          student={student}
          agreement={linking}
          onClose={() => setLinking(null)}
          onChanged={(a) => setAgreements((prev) => prev.map((r) => r.id === a.id ? a : r))}
        />
      )}
    </>
  );
}

function AgreementRow({ a, canEdit, onEdit, onOnec }) {
  const meta = TYPE_META[a.type] || TYPE_META.school;
  const onec = a.onec || {};
  const today = todayIsoStr();

  // Период действия: «дата начала — дата окончания», если обе есть; иначе дата договора
  const periodLabel = (a.dateStart && a.dateEnd)
    ? `${fmtDateRuShort(a.dateStart)} — ${fmtDateRuShort(a.dateEnd)}`
    : (a.date ? fmtDateRuShort(a.date) : '');

  // Статус: аннулирован/расторгнут (красный) → истёк (серый) → ожидает (жёлтый, если начало в будущем) → действует (зелёный)
  let pillClass = 'agr-row__pill--ok', pillLabel = 'действует', pillTitle = '';
  if (a.dateTermination) {
    // Расторжение датой не позже начала действия = договор не вступил
    // в силу ни на один день — показываем как «аннулирован».
    const annulled = a.dateStart && a.dateTermination <= a.dateStart;
    pillClass = 'agr-row__pill--red';
    pillLabel = annulled ? 'аннулирован' : 'расторгнут';
    const verb = annulled ? 'Аннулирован' : 'Расторгнут';
    pillTitle = a.terminationReason
      ? `${verb} ${fmtDateRuShort(a.dateTermination)} — ${a.terminationReason}`
      : `${verb} ${fmtDateRuShort(a.dateTermination)}`;
  } else if (a.dateEnd && a.dateEnd < today) {
    pillClass = 'agr-row__pill--gray';
    pillLabel = 'истёк';
    pillTitle = `Договор истёк ${fmtDateRuShort(a.dateEnd)}`;
  } else if (a.dateStart && a.dateStart > today) {
    pillClass = 'agr-row__pill--yellow';
    pillLabel = 'ожидает';
    pillTitle = `Начало действия ${fmtDateRuShort(a.dateStart)}`;
  }

  return (
    <div className="agr-row">
      <div className={'agr-row__icon agr-row__icon--' + a.type}>
        <Icon name={meta.icon} size={14} />
      </div>
      <div className="agr-row__main">
        <div className="agr-row__head">
          <span className="agr-row__title">{meta.title}</span>
          <span className="agr-row__type-pill">{meta.label}</span>
          {a.type === 'school' && a.class != null && (
            <span className="agr-row__class-pill">{a.class} класс</span>
          )}
        </div>
        <div className="agr-row__meta">
          <span className="agr-row__num">№ {a.displayNumber || a.number}</span>
          {periodLabel && <span className="agr-row__period">{periodLabel}</span>}
          <span className={'agr-row__pill ' + pillClass} title={pillTitle}>{pillLabel}</span>
          {onec.code && (
            <span className="agr-row__pill agr-row__pill--onec" title="Привязан к 1С">
              ∞ 1С: {onec.code}
            </span>
          )}
          {onec.syncState === 'pending_create' && (
            <span className="agr-row__pending" title={onec.syncError || 'Ожидает 1С'}>ожидает 1С</span>
          )}
          {onec.syncState === 'error' && (
            <span className="agr-row__error" title={onec.syncError || 'Ошибка'}>ошибка</span>
          )}
          {!onec.agreementId && (
            <span className="agr-row__error" title="Договор не привязан к 1С">без 1С</span>
          )}
        </div>
      </div>
      {canEdit && (
        <div className="ref-row-actions">
          <AgreementTemplatesMenu agreement={a} />
          {a.publicPayUrl && (
            <CopyPayLinkButton url={a.publicPayUrl} />
          )}
          <button
            type="button"
            onClick={onOnec}
            title={onec.agreementId ? 'Связь с 1С' : 'Привязать к 1С'}
          >
            <Icon name={onec.agreementId ? 'link' : 'link-off'} size={14} />
          </button>
          <button type="button" onClick={onEdit} title="Редактировать">
            <Icon name="edit" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Кнопка-«копирка» для публичной ссылки оплаты. Через 1.2 сек
 * возвращается в исходное состояние — короткая обратная связь.
 * На случай отсутствия Clipboard API (старый браузер / http) — фолбэк
 * через временный textarea + document.execCommand('copy').
 */
function CopyPayLinkButton({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // если ничего не сработало — покажем ссылку, чтобы оператор скопировал руками
      window.prompt('Скопируйте ссылку:', url);
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Скопировано' : ('Скопировать ссылку оплаты: ' + url)}
      style={copied ? { color: 'var(--green-700)' } : undefined}
    >
      <Icon name={copied ? 'check' : 'external'} size={14} />
    </button>
  );
}

// Список Word-шаблонов, доступных для договора. Дублирует AgreementFormModal —
// держать в одном месте можно после первого рефакторинга, пока пусть будет.
const AGREEMENT_TEMPLATES = {
  school: [
    { code: 'agreement_school',                         label: 'Договор обучения' },
    { code: 'application_for_admission__school',        label: 'Заявление на приём' },
    { code: 'agreement_termination',                    label: 'Заявление на расторжение' },
  ],
  kindergarten: [
    { code: 'agreement_kindergarten',                   label: 'Договор обучения' },
    { code: 'application_for_admission__kindergarten',  label: 'Заявление на приём' },
    { code: 'agreement_termination',                    label: 'Заявление на расторжение' },
  ],
  paid_services: [
    { code: 'agreement_paid_services',                  label: 'Договор на доп. услуги' },
    { code: 'agreement_termination',                    label: 'Заявление на расторжение' },
  ],
};

function AgreementTemplatesMenu({ agreement }) {
  const list = AGREEMENT_TEMPLATES[agreement?.type] || [];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!list.length) return null;
  return (
    <div className="agr-tpl-menu" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Скачать документ"
        className={open ? 'is-active' : undefined}
      >
        <Icon name="doc" size={14} />
      </button>
      {open && (
        <div className="agr-tpl-menu__pop">
          {list.map((t) => (
            <a
              key={t.code}
              href={`/api/agreements/${agreement.id}/template/${t.code}`}
              className="agr-tpl-menu__item"
              onClick={() => setOpen(false)}
            >
              <Icon name="download" size={12} />
              <span>{t.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Блок «Контактные лица» в боковой колонке. Источник — единая таблица contacts;
 * один и тот же контакт может быть и просто «контактным лицом», и заказчиком в договоре.
 *
 * Показывает: аватар-инициалы, роль (свободный текст, например «Мама»), флаги
 * («основной/финансы/забирает»), телефон/email, отметку «нужно дозаполнить» если
 * контакт ещё не годится для договора.
 */
function ContactsSection({ studentId, canEdit, initialContacts }) {
  const [contacts, setContacts] = useState(initialContacts || []);
  const [editing, setEditing]   = useState(null); // null | 'new' | object

  const onDelete = async (c) => {
    if (!confirm(`Удалить контакт «${c.fullName}»?`)) return;
    try {
      await api.delete(`/api/contacts/${c.id}`);
      setContacts((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      alert('Не удалось удалить: ' + (e.message || ''));
    }
  };

  return (
    <div className="sd-section">
      <div className="sd-section__title">
        Контактные лица
        {canEdit && (
          <button onClick={() => setEditing('new')}>+ Добавить</button>
        )}
      </div>

      {contacts.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
          Контактов нет.
        </div>
      ) : contacts.map((c) => {
        const initials = ((c.familyName?.[0] || '') + (c.firstName?.[0] || '')).toUpperCase() || '?';
        const incomplete = c.missingForSignatory && c.missingForSignatory.length > 0;
        return (
          <div key={c.id} className={'sd-contact' + (c.isPrimary ? ' is-primary' : '')}>
            <div className="sd-contact__avatar">{initials}</div>
            <div className="sd-contact__main">
              <div className="sd-contact__role" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {c.role || 'Контакт'}
                {c.isPrimary && <span className="sd-contact__primary">основной</span>}
                {canEdit && (
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 2 }}>
                    <button
                      type="button"
                      onClick={() => setEditing(c)}
                      title="Редактировать"
                      style={{ width: 22, height: 22, display: 'grid', placeItems: 'center', borderRadius: 4, color: 'var(--fg-faint)' }}
                    >
                      <Icon name="edit" size={11} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(c)}
                      title="Удалить"
                      style={{ width: 22, height: 22, display: 'grid', placeItems: 'center', borderRadius: 4, color: 'var(--fg-faint)' }}
                    >
                      <Icon name="x" size={11} />
                    </button>
                  </span>
                )}
              </div>
              <div className="sd-contact__name">{c.fullName}</div>
              {c.phone && <div className="sd-contact__phone">{c.phone}</div>}
              {c.email && <div className="sd-contact__email">{c.email}</div>}
              {(c.isFinance || c.canPickup) && (
                <div className="sd-contact__flags" style={{ marginTop: 4 }}>
                  {c.isFinance && <span className="sd-flag sd-flag--on">финансы</span>}
                  {c.canPickup && <span className="sd-flag sd-flag--on">забирает</span>}
                </div>
              )}
              {incomplete && (
                <div style={{ fontSize: 11, color: 'var(--amber-fg)', marginTop: 4 }}>
                  Для договора: дозаполните {c.missingForSignatory.length} полей
                </div>
              )}
            </div>
          </div>
        );
      })}

      {editing && (
        <ContactEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          studentId={studentId}
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(c) => {
            setContacts((prev) => {
              const idx = prev.findIndex((x) => x.id === c.id);
              if (idx >= 0) {
                const next = prev.slice();
                next[idx] = c;
                return next;
              }
              return [...prev, c];
            });
          }}
        />
      )}
    </div>
  );
}

function OnecBadge({ contractor }) {
  if (!contractor) {
    return (
      <span className="sd-onec is-off" title="Не привязан к контрагенту 1С">
        1С: не привязан
      </span>
    );
  }
  if (contractor.syncState === 'pending_create') {
    return (
      <span className="sd-onec" style={{ color: 'var(--amber-fg)' }} title={`Контрагент ${contractor.name} создан локально, ждёт отправки в 1С`}>
        1С: ожидает (#{contractor.id})
      </span>
    );
  }
  if (contractor.syncState === 'error') {
    return (
      <span className="sd-onec" style={{ color: 'var(--red-fg)' }} title={contractor.syncError || 'Ошибка синка с 1С'}>
        1С: ошибка
      </span>
    );
  }
  return (
    <span className="sd-onec is-on" title={`Привязка к 1С: ${contractor.code} — ${contractor.name}`}>
      1С: {contractor.code}
    </span>
  );
}

function Empty({ icon, title, text }) {
  return (
    <div className="sd-empty">
      <div style={{ marginBottom: 12, opacity: 0.5 }}><Icon name={icon} size={32} /></div>
      <div className="sd-empty__title">{title}</div>
      <div style={{ fontSize: 12.5 }}>{text}</div>
    </div>
  );
}

/* ─────────────────────── История ─────────────────────── */
/* Тексты/иконки событий — общий модуль @/lib/studentEvents (используется
   также отчётом «Действия пользователей»). */

/**
 * Вкладка «История»: лента событий по ученику (кто, когда и что сделал).
 * Данные — GET /api/students/{id}/events; события пишутся контроллерами
 * (договоры, зачисления, контакты, карточка). Старые данные бэкфилятся
 * миграцией и подписываются «импорт данных».
 */
function HistoryTab({ studentId }) {
  const [rows, setRows]   = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let aborted = false;
    setRows(null); setError(null);
    api.get(`/api/students/${studentId}/events`)
      .then((r) => { if (!aborted) setRows(r.rows || []); })
      .catch((e) => { if (!aborted) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить историю'); });
    return () => { aborted = true; };
  }, [studentId]);

  if (error)         return <div style={{ padding: 24, color: 'var(--red-fg)' }}>{error}</div>;
  if (rows === null) return <div style={{ padding: 24, color: 'var(--fg-muted)' }}>Загрузка…</div>;
  if (rows.length === 0) {
    return <Empty icon="archive" title="История" text="Событий пока нет — они появятся при изменениях по ученику." />;
  }

  return (
    <div style={{ padding: '4px 0' }}>
      {rows.map((e) => (
        <div key={e.id} style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          padding: '9px 4px', borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ color: 'var(--fg-faint)', flexShrink: 0, marginTop: 1 }}>
            <Icon name={eventIcon(e.type)} size={14} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, lineHeight: 1.4 }}>{eventText(e)}</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', marginTop: 2 }}>
              {fmtEventDateTime(e.createdAt)}
              {' · '}
              {e.user ? e.user.name : (e.payload?.backfill ? 'импорт данных' : 'система')}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
